// WorkspaceShell — single-API provider workspace layout.
//
// 5-段式左侧导航（参考 RapidAPI 的 provider studio）：
//   basic     基本信息
//   apis      管理 API（endpoints + 导入 + 版本）
//   tests     自动化测试（Coming soon）
//   analytics 分析（traffic / users / revenue 三个子段）
//   settings  设置（危险操作）
//
// 本组件只负责"外壳 + 导航"，每段的具体内容由调用方通过 children 传入。
// 这样 Phase 1 各 task 可以独立填充各段而不互相阻塞。

import React from 'react';
import { XIcons } from '../primitives.jsx';
import { XShell } from '../shell.jsx';
import { Menu, MenuItem, MenuSeparator } from './menu.jsx';

const NAV = [
  { key: 'basic',     label: '基本信息',   Icon: XIcons.IconInfo },
  { key: 'apis',      label: '管理 API',   Icon: XIcons.IconCode },
  { key: 'skill',     label: 'Skill',      Icon: XIcons.IconLayers },
  { key: 'tests',     label: '自动化测试', Icon: XIcons.IconPlay },
  { key: 'analytics', label: '分析',       Icon: XIcons.IconBarChart },
  { key: 'anomalies', label: '告警',       Icon: XIcons.IconBell }, // D5
  { key: 'settings',  label: '设置',       Icon: XIcons.IconSettings },
];

const ANALYTICS_SUB = [
  { key: 'traffic', label: 'Traffic' },
  { key: 'users',   label: 'Users' },
  { key: 'revenue', label: 'Revenue' },
];

// 头部状态徽标：默认大版本的派生 label（active/superseded 由 serving 指针派生，见后端）。
const STATUS_LABELS = {
  active:     { text: '已上线', bg: 'rgba(34,197,94,0.15)', fg: '#16a34a' },
  sandbox:    { text: '沙箱',   bg: 'rgba(234,179,8,0.15)', fg: '#b45309' },
  draft:      { text: '草稿',   bg: 'var(--bg-1)',          fg: 'var(--fg-3)' },
  in_review:  { text: '审核中', bg: 'rgba(59,130,246,0.15)', fg: '#2563eb' },
  superseded: { text: '历史',   bg: 'var(--bg-1)',          fg: 'var(--fg-4)' },
  suspended:  { text: '已暂停', bg: 'rgba(239,68,68,0.15)', fg: '#dc2626' },
};
const WORKING_TEXT = { DRAFT: '草稿', SANDBOX: '沙箱', IN_REVIEW: '审核中' };

const StatusChip = ({ status }) => {
  const m = STATUS_LABELS[status] || { text: status, bg: 'var(--bg-1)', fg: 'var(--fg-3)' };
  return (
    <span style={{ padding: '1px 7px', borderRadius: 9, fontSize: 10.5, fontWeight: 600, background: m.bg, color: m.fg }}>
      {m.text}
    </span>
  );
};

export const WorkspaceShell = ({
  api,
  activeSection,
  activeSubsection,
  onSelect,
  children,
  versionOverview,
  selectedMajor,
  onSelectMajor,
  onOpenVersionManager,
}) => {
  const { navigate } = XShell.useRoute();

  // D5+D2: fetch unread anomaly + alert notification count → red badge on 告警 tab
  // 一个数字总和：未解决的异常 + 未读的通知。
  const [anomalyUnread, setAnomalyUnread] = React.useState(0);
  React.useEffect(() => {
    if (!api?.id) return;
    let cancelled = false;
    import('../services.js').then(({ XApi }) => {
      Promise.all([
        XApi.providerDashboardService.anomalies({ resolved: 'open', apiId: api.id, limit: 1 }).catch(() => null),
        XApi.providerDashboardService.listAlertNotifications({ unreadOnly: true, limit: 1 }).catch(() => null),
      ]).then(([anom, notifs]) => {
        if (cancelled) return;
        setAnomalyUnread((anom?.unreadCount ?? 0) + (notifs?.unreadCount ?? 0));
      });
    });
    return () => { cancelled = true; };
  }, [api?.id, activeSection]);

  // 头部：版本切换器读 versionOverview（由 workspace-api 提供，单一数据源）。
  // 当前选中大版本的派生状态用于状态徽标（active/sandbox/...）。
  const majors = versionOverview?.majors || [];
  const selMajor =
    majors.find((m) => m.major === selectedMajor) ||
    majors.find((m) => m.isDefault) ||
    majors[0] ||
    null;

  return (
    <div style={{ display: 'flex', minHeight: 'calc(100vh - 64px)' }}>
      <aside className="ws-aside">
        <button type="button" className="ws-back" onClick={() => navigate('workspace')}>
          {XIcons.IconArrowLeft && <XIcons.IconArrowLeft size={12} />}
          <span>My APIs</span>
        </button>

        {/* 项目身份：头像 + 名称 + 类别 */}
        <div className="ws-id">
          <div className="ws-id-avatar">
            {api?.logoUrl
              ? <img src={api.logoUrl} alt="" />
              : <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--fg-2)' }}>{(api?.name || '?').trim().charAt(0).toUpperCase()}</span>}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="ws-id-name" title={api?.name || ''}>{api?.name || '—'}</div>
            <div className="ws-id-sub">{api?.category || 'API 服务'}</div>
          </div>
        </div>

        {/* 版本切换器：选哪个大版本，编辑/保存/发布就落到那个版本。底部「管理版本…」。 */}
        {selMajor ? (
          <Menu
            align="start"
            minWidth={216}
            triggerStyle={{ all: 'unset', display: 'block', width: '100%', cursor: 'pointer' }}
            trigger={
              <span className="ws-ver-trigger">
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--fg-0)' }}>{selMajor.label}</span>
                <StatusChip status={selMajor.status} />
                {selMajor.working && <span title="有未发布的改动（草稿）" style={{ fontSize: 12, color: '#b45309' }}>✎</span>}
                <span style={{ flex: 1 }} />
                {XIcons.IconChevronDown
                  ? <XIcons.IconChevronDown size={16} style={{ color: 'var(--fg-2)', flexShrink: 0 }} />
                  : <span style={{ fontSize: 14, color: 'var(--fg-2)' }}>▾</span>}
              </span>
            }
          >
            {majors.map((m) => (
              <MenuItem key={m.major} onClick={() => onSelectMajor?.(m.major)}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, width: '100%' }}>
                  <span style={{ width: 10, color: 'var(--accent)' }}>{m.major === selMajor.major ? '✓' : ''}</span>
                  <span style={{ flex: 1 }}>{m.label}</span>
                  <span style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{STATUS_LABELS[m.status]?.text || m.status}</span>
                  {m.isDefault && <span style={{ fontSize: 10, color: 'var(--fg-4)' }}>默认</span>}
                </span>
              </MenuItem>
            ))}
            <MenuSeparator />
            <MenuItem icon={XIcons.IconSettings} onClick={() => onOpenVersionManager?.()}>管理版本…</MenuItem>
          </Menu>
        ) : (
          <div style={{ padding: '0 4px', fontSize: 11, color: 'var(--fg-3)' }}>
            {api?.status || ''}{api?.activeVersion?.version ? ` · ${api.activeVersion.version}` : ''}
          </div>
        )}
        {selMajor?.live && selMajor?.working && (() => {
          // 提示随工作副本的审核态走，避免与顶部状态条「审核中/已驳回」矛盾。
          const oc = selMajor.working.review?.outcome;
          const m = oc === 'reviewing'
            ? { c: '#2563eb', t: '审核中 · 通过后自动上线' }
            : oc === 'rejected'
              ? { c: '#dc2626', t: '审核未通过 · 修复后重新发布' }
              : { c: '#b45309', t: '有未发布的改动 · 点「发布」上线' };
          return (
            <div style={{ marginTop: 6, padding: '0 4px', fontSize: 11, color: m.c, display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.c, flexShrink: 0 }} />
              <span>{m.t}</span>
            </div>
          );
        })()}

        <div className="ws-divider" />
        <div className="ws-nav-label">工作区</div>

        <nav>
          {NAV.map((it) => {
            const active = activeSection === it.key;
            const Icon = it.Icon;
            return (
              <React.Fragment key={it.key}>
                <button type="button" className={`ws-nav-item${active ? ' is-active' : ''}`} onClick={() => onSelect(it.key)}>
                  {Icon && <span className="ws-nav-ico"><Icon size={15} /></span>}
                  <span style={{ flex: 1 }}>{it.label}</span>
                  {/* D5 unread badge on 告警 tab */}
                  {it.key === 'anomalies' && anomalyUnread > 0 && (
                    <span style={{ background: '#c0392b', color: '#fff', fontSize: 10, fontWeight: 500, padding: '1px 6px', borderRadius: 999, lineHeight: 1.2 }}>{anomalyUnread > 99 ? '99+' : anomalyUnread}</span>
                  )}
                </button>
                {active && it.key === 'analytics' && (
                  <div className="ws-subnav">
                    {ANALYTICS_SUB.map((sub) => {
                      const subActive = (activeSubsection || 'traffic') === sub.key;
                      return (
                        <button key={sub.key} type="button" className={`ws-subnav-item${subActive ? ' is-active' : ''}`} onClick={() => onSelect('analytics', sub.key)}>
                          {sub.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      </aside>

      <main style={{ flex: 1, minWidth: 0, borderLeft: '1px solid var(--bd-1)' }}>{children}</main>
    </div>
  );
};
